import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ListPlus, LogOut, Plus, Search, UserRound } from "lucide-react";
import { useNavigate } from "react-router";

import { useCan } from "@/app/session/permissions";
import { useCurrentUser, useSession } from "@/app/session/useSession";
import { NotificationBell } from "@/features/notifications/components/NotificationBell";
import type { Locale } from "@/shared/i18n/messages";
import { useLocale, useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { MOD_KEY } from "@/shared/lib/platform";
import { THEME_OPTIONS } from "@/shared/theme/options";
import { useTheme } from "@/shared/theme/useTheme";
import { Avatar } from "@/shared/ui/Avatar";
import { Kbd } from "@/shared/ui/Kbd";
import { menuContentClassName, menuItemClassName } from "@/shared/ui/styles";
import { Tooltip } from "@/shared/ui/Tooltip";

interface DesktopHeaderProps {
  onSearch: () => void;
  onNewProject: () => void;
  onNewTask: () => void;
}

const SEGMENT_GROUP =
  "flex h-11 items-stretch gap-0.5 rounded-xl border border-line bg-surface-sunken p-1";

/* One option in a segmented switch: the chosen one rises onto a white surface. */
function segmentClass(active: boolean) {
  return cn(
    "flex h-full items-center justify-center gap-1.5 rounded-lg px-3 text-[14px] font-medium transition-[background-color,color,box-shadow] duration-150 [&_svg]:size-[18px]",
    active ? "bg-surface text-ink shadow-sm ring-1 ring-line" : "text-ink-subtle hover:text-ink",
  );
}

function HeaderSearch({ onSearch }: { onSearch: () => void }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onSearch}
      className="group/search flex h-11 w-full min-w-44 max-w-md items-center gap-3 rounded-xl border border-line bg-surface ps-4 pe-2 text-[15px] text-ink-subtle shadow-xs transition-[border-color,box-shadow] hover:border-line-strong hover:shadow-sm"
    >
      <Search
        className="size-[18px] shrink-0 text-ink-faint transition-colors group-hover/search:text-ink-muted"
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1 truncate text-start">{t("topbar.search")}</span>
      <span dir="ltr" className="hidden shrink-0 gap-0.5 xl:flex">
        <Kbd>{MOD_KEY}</Kbd>
        <Kbd>K</Kbd>
      </span>
    </button>
  );
}

const LANGUAGES: readonly { value: Locale; label: string }[] = [
  { value: "ar", label: "العربية" },
  { value: "en", label: "English" },
];

/** Arabic or English, both always on screen, each written in its own language. */
function LanguageSwitch() {
  const t = useT();
  const { locale, setLocale } = useLocale();
  return (
    <div role="radiogroup" aria-label={t("header.language")} className={SEGMENT_GROUP}>
      {LANGUAGES.map((language) => {
        const active = locale === language.value;
        return (
          <button
            key={language.value}
            type="button"
            role="radio"
            aria-checked={active}
            lang={language.value}
            dir={language.value === "ar" ? "rtl" : "ltr"}
            onClick={() => {
              setLocale(language.value);
            }}
            className={segmentClass(active)}
          >
            {language.label}
          </button>
        );
      })}
    </div>
  );
}

/** System, light or dark, as three icons with their names in tooltips. */
function ThemeSwitch() {
  const t = useT();
  const { preference, setPreference } = useTheme();
  return (
    <div role="radiogroup" aria-label={t("theme.title")} className={SEGMENT_GROUP}>
      {THEME_OPTIONS.map((option) => {
        const Icon = option.icon;
        const active = preference === option.value;
        const label = t(option.labelId);
        return (
          <Tooltip key={option.value} label={label} side="bottom" className="h-full">
            <button
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={label}
              onClick={() => {
                setPreference(option.value);
              }}
              className={cn(segmentClass(active), "h-full w-10 px-0")}
            >
              <Icon aria-hidden="true" />
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}

const ACCOUNT_ITEM = cn(menuItemClassName, "[&_svg]:text-ink-muted");

/** The signed-in person: click for the profile and to sign out. */
function Account() {
  const t = useT();
  const user = useCurrentUser();
  const { signOut } = useSession();
  const navigate = useNavigate();
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        title={user.name}
        aria-label={user.name}
        className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-line bg-surface shadow-xs outline-none transition-[border-color,box-shadow] hover:border-line-strong hover:shadow-sm data-[state=open]:border-line-strong data-[state=open]:shadow-sm"
      >
        <Avatar name={user.name} src={user.avatar_url} className="size-8 text-[13px]" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className={cn(menuContentClassName, "min-w-52")}
        >
          <DropdownMenu.Item
            onSelect={() => {
              void navigate("/settings/profile");
            }}
            className={ACCOUNT_ITEM}
          >
            <UserRound aria-hidden="true" />
            {t("profile.title")}
          </DropdownMenu.Item>
          <DropdownMenu.Item onSelect={() => void signOut()} className={ACCOUNT_ITEM}>
            <LogOut className="rtl:-scale-x-100" aria-hidden="true" />
            {t("auth.signOut")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * The desktop header above every page: search, a new task and a new project, language, theme,
 * notifications and the account, always in view instead of tucked into menus. Phones keep their
 * own top bar.
 */
export function DesktopHeader({ onSearch, onNewProject, onNewTask }: DesktopHeaderProps) {
  const t = useT();
  const canCreate = useCan("projects.create");
  const canCreateTasks = useCan("tasks.create");
  return (
    <header className="mb-2 hidden h-16 shrink-0 items-center gap-3 rounded-2xl border border-line bg-surface/80 px-3 shadow-xs backdrop-blur lg:flex">
      <HeaderSearch onSearch={onSearch} />
      <div className="ms-auto flex items-center gap-2">
        {canCreateTasks ? (
          <button
            type="button"
            onClick={onNewTask}
            title={t("newTask.title")}
            className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-line bg-surface max-xl:w-11 xl:ps-3.5 xl:pe-4 text-[14.5px] font-semibold whitespace-nowrap text-ink shadow-xs transition-[border-color,box-shadow] hover:border-line-strong hover:shadow-sm [&_svg]:size-[18px] [&_svg]:shrink-0"
          >
            <ListPlus aria-hidden="true" />
            <span className="sr-only xl:not-sr-only">{t("newTask.title")}</span>
          </button>
        ) : null}
        {canCreate ? (
          <button
            type="button"
            onClick={onNewProject}
            title={t("projects.new")}
            className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-ink max-xl:w-11 xl:ps-3.5 xl:pe-4 text-[14.5px] font-semibold whitespace-nowrap text-ink-inverse shadow-sm transition-colors hover:bg-ink/88 [&_svg]:size-[18px] [&_svg]:shrink-0"
          >
            <Plus aria-hidden="true" />
            <span className="sr-only xl:not-sr-only">{t("projects.new")}</span>
          </button>
        ) : null}
        <LanguageSwitch />
        <ThemeSwitch />
        <NotificationBell
          presentation="popover"
          className="size-11 rounded-xl border border-line bg-surface shadow-xs"
        />
        <Account />
      </div>
    </header>
  );
}
